"use client";

import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  MailOpen,
  User,
  Mail,
  Clock,
  FileText,
  Loader2,
  UserCheck,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Bot,
  ExternalLink,
} from "lucide-react";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type ThreadHistoryMessage = {
  id: number;
  from_email: string;
  body_text: string;
  body_html: string | null;
  received_at: string;
  is_inbound: boolean;
  is_agent_reply?: boolean;
};

export type EmailDetail = {
  id: string;
  externalId: string;
  threadId: string;
  inboxId?: string;
  snippet: string;
  from: string;
  subject: string;
  date: string;
  receivedAt?: string;
  to?: string;
  headers?: Record<string, string>;
  bodyText?: string;
  bodyHtml?: string;
  body_text?: string;
  body_html?: string;
  overrideIdentity?: string;
  crmExternalUrl?: string | null;
  threadHistory?: ThreadHistoryMessage[];
  threadAfterMessages?: ThreadHistoryMessage[];
};

interface EmailViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  email: EmailDetail | null;
  userTimezone?: string;
  showCrmLink?: boolean;
}

function formatDateTz(dateString: string, timezone?: string, dateLocale: string = "en-US") {
  try {
    const opts: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...(timezone ? { timeZone: timezone } : {}),
    };
    return new Date(dateString).toLocaleDateString(dateLocale, opts);
  } catch {
    return dateString;
  }
}

export function EmailViewerDialog({
  open,
  onOpenChange,
  email,
  userTimezone,
  showCrmLink,
}: EmailViewerDialogProps) {
  const {locale: uiDateLocale} = useUiControlLabels()


  const tUi = useTranslations("Email.AgentsEmailDialogsEmailViewerDialog")

  const [beforeExpanded, setBeforeExpanded] = useState(false);
  const [afterExpanded, setAfterExpanded] = useState(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg font-semibold pr-8">
            <div className="flex items-center gap-2">
              <MailOpen className="w-5 h-5 text-blue-600" />
              <span className="truncate">
                {email?.subject || tUi("emailMessage")}
              </span>
            </div>
          </DialogTitle>
        </DialogHeader>
        {!email ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600"><span key={tUi("loadingEmail")}>{tUi("loadingEmail")}</span></span>
          </div>
        ) : (
          <ScrollArea className="flex-1 overflow-hidden">
            <div className="space-y-6 pr-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      <span key={tUi("from")}>{tUi("from")}</span></span>
                    {email.id && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        #{email.id}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-900 font-medium">
                    {email.from || tUi("unknownSender")}
                  </div>
                  {email.overrideIdentity && (
                    <div className="flex items-center gap-1 mt-1">
                      <UserCheck className="w-3 h-3 text-blue-600" />
                      <span className="text-xs text-blue-600"><TranslationText>{tUi.rich("overrideValue0", { value0: String(email.overrideIdentity) })}</TranslationText></span>
                    </div>
                  )}
                </div>
                {email.to && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        <span key={tUi("to")}>{tUi("to")}</span></span>
                    </div>
                    <div className="text-sm text-gray-900">{email.to}</div>
                  </div>
                )}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      <span key={tUi("date")}>{tUi("date")}</span></span>
                  </div>
                  <div key={email.date ? formatDateTz(email.date, userTimezone, uiDateLocale) : tUi("unknownDate")} className="text-sm text-gray-900">
                    {email.date ? formatDateTz(email.date, userTimezone, uiDateLocale) : tUi("unknownDate")}
                  </div>
                </div>
              </div>

              {/* Thread Before */}
              {email.threadHistory && email.threadHistory.length > 0 && (
                <Collapsible open={beforeExpanded} onOpenChange={setBeforeExpanded}>
                  <div className="border rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full p-3 flex items-center justify-between bg-green-50 hover:bg-green-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          <span key={tUi("threadBefore")}>{tUi("threadBefore")}</span></span>
                        <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                          {email.threadHistory.length}
                        </Badge>
                      </div>
                      {beforeExpanded
                        ? <ChevronUp className="w-4 h-4 text-green-600" />
                        : <ChevronDown className="w-4 h-4 text-green-600" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 space-y-3 bg-white">
                        {email.threadHistory.map((msg) => (
                          <div
                            key={msg.id}
                            className={cn(
                              "flex",
                              msg.is_inbound ? "justify-start" : "justify-end",
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-[80%] rounded-lg p-3 space-y-1",
                                msg.is_inbound
                                  ? "bg-gray-100 text-gray-900"
                                  : "bg-blue-100 text-blue-900",
                              )}
                            >
                              <div className="flex items-center gap-2 text-xs opacity-70">
                                <span className="font-medium flex items-center gap-1">
                                  {msg.is_inbound ? msg.from_email : (
                                    msg.is_agent_reply ? (
                                      <><TranslationText>{tUi.rich("tag0Tag0AIAgent", { tag0: () => (<Bot className="w-3 h-3" />) })}</TranslationText></>
                                    ) : (
                                      <><TranslationText>{tUi.rich("tag0Tag0Human", { tag0: () => (<User className="w-3 h-3" />) })}</TranslationText></>
                                    )
                                  )}
                                </span>
                                <span>{formatDateTz(msg.received_at, userTimezone, uiDateLocale)}</span>
                              </div>
                              {msg.body_html ? (
                                <div
                                  className="prose prose-sm max-w-none text-sm"
                                  dangerouslySetInnerHTML={{ __html: msg.body_html }}
                                />
                              ) : (
                                <pre className="whitespace-pre-wrap text-sm font-sans">
                                  {msg.body_text}
                                </pre>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              <div className="border-2 border-orange-300 rounded-lg overflow-hidden">
                <div className="p-3 flex items-center justify-between bg-orange-50">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="text-sm font-semibold text-orange-800">
                      <span key={tUi("emailContent")}>{tUi("emailContent")}</span></span>
                    {email.receivedAt && (
                      <span className="text-xs text-orange-600">
                        {formatDateTz(email.receivedAt, userTimezone, uiDateLocale)}
                      </span>
                    )}
                  </div>
                  {showCrmLink && email.crmExternalUrl && (
                    <a
                      href={email.crmExternalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      <span><span key={"CRM"}>{"CRM"}</span></span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="p-4 bg-white">
                  {email.bodyHtml || email.body_html ? (
                    <div
                      className="prose prose-sm max-w-none text-sm overflow-x-auto"
                      dangerouslySetInnerHTML={{
                        __html: email.bodyHtml || email.body_html || "",
                      }}
                    />
                  ) : (
                    <pre className="whitespace-pre-wrap text-sm font-mono">
                      {email.bodyText ||
                        email.body_text ||
                        email.snippet ||
                        tUi("noContentAvailable")}
                    </pre>
                  )}
                </div>
              </div>

              {/* Thread After */}
              {email.threadAfterMessages && email.threadAfterMessages.length > 0 && (
                <Collapsible open={afterExpanded} onOpenChange={setAfterExpanded}>
                  <div className="border rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full p-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          <span key={tUi("threadAfter")}>{tUi("threadAfter")}</span></span>
                        <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">
                          {email.threadAfterMessages.length}
                        </Badge>
                      </div>
                      {afterExpanded
                        ? <ChevronUp className="w-4 h-4 text-blue-600" />
                        : <ChevronDown className="w-4 h-4 text-blue-600" />}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 space-y-3 bg-white">
                        {email.threadAfterMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={cn(
                              "flex",
                              msg.is_inbound ? "justify-start" : "justify-end",
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-[80%] rounded-lg p-3 space-y-1",
                                msg.is_inbound
                                  ? "bg-gray-100 text-gray-900"
                                  : "bg-blue-100 text-blue-900",
                              )}
                            >
                              <div className="flex items-center gap-2 text-xs opacity-70">
                                <span className="font-medium flex items-center gap-1">
                                  {msg.is_inbound ? msg.from_email : (
                                    msg.is_agent_reply ? (
                                      <><TranslationText>{tUi.rich("tag0Tag0AIAgent", { tag0: () => (<Bot className="w-3 h-3" />) })}</TranslationText></>
                                    ) : (
                                      <><TranslationText>{tUi.rich("tag0Tag0Human", { tag0: () => (<User className="w-3 h-3" />) })}</TranslationText></>
                                    )
                                  )}
                                </span>
                                <span>{formatDateTz(msg.received_at, userTimezone, uiDateLocale)}</span>
                              </div>
                              {msg.body_html ? (
                                <div
                                  className="prose prose-sm max-w-none text-sm"
                                  dangerouslySetInnerHTML={{ __html: msg.body_html }}
                                />
                              ) : (
                                <pre className="whitespace-pre-wrap text-sm font-sans">
                                  {msg.body_text}
                                </pre>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}
            </div>
          </ScrollArea>
        )}

      </DialogContent>
    </Dialog>
  );
}
